import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import type { CustomFilterProps } from 'ag-grid-react';
import type { IRowNode } from 'ag-grid-community';
import { useGridFilter } from 'ag-grid-react';
import {
  GRID_FILTER_OPTION_PAGE_SIZE,
  loadGridFilterOptions,
  staticGridFilterOptions,
  type GridFilterOption,
  type GridFilterOptionRequest,
  type GridFilterOptionValue,
} from '../../utils/gridFilterOptions';
import './AsyncCheckboxFilter.css';

/**
 * No model = every value ticked (no filter). `excluded` = everything except
 * `values`, so a long server-backed column can start from "all" without
 * loading every choice. Otherwise only `values` are ticked; an empty list is
 * Deselect all and passes no row (REG-800).
 */
export interface AsyncCheckboxFilterModel {
  filterType: 'set';
  values: GridFilterOptionValue[];
  excluded?: boolean;
}

export interface AsyncCheckboxFilterParams {
  optionRequest?: GridFilterOptionRequest;
  staticOptions?: GridFilterOption[];
}

type Props = CustomFilterProps<Record<string, unknown>, unknown, AsyncCheckboxFilterModel>
  & AsyncCheckboxFilterParams;

function valueKey(value: GridFilterOptionValue): string {
  return JSON.stringify(value);
}

function scalarFilterValue(value: unknown): GridFilterOptionValue | undefined {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Date) return value.toISOString();
  return undefined;
}

export function AsyncCheckboxFilter({
  model,
  onModelChange,
  getValue,
  api,
  column,
  optionRequest,
  staticOptions,
}: Props): ReactElement {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [options, setOptions] = useState<GridFilterOption[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestGeneration = useRef(0);
  const listedValues = useMemo(() => model?.values ?? [], [model?.values]);
  const listedKeys = useMemo(() => new Set(listedValues.map(valueKey)), [listedValues]);
  const allTicked = model == null;
  const excluding = model?.excluded === true;
  const isTicked = useCallback((key: string) => (
    allTicked || (excluding ? !listedKeys.has(key) : listedKeys.has(key))
  ), [allTicked, excluding, listedKeys]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), 200);
    return () => window.clearTimeout(timer);
  }, [search]);

  const fetchPage = useCallback(async (offset: number, append: boolean) => {
    const generation = ++requestGeneration.current;
    setLoading(true);
    setError(null);
    try {
      let clientOptions: GridFilterOption[] | undefined;
      if (!staticOptions && !optionRequest) {
        const values = new Map<string, GridFilterOption>();
        api.forEachLeafNode((node: IRowNode<Record<string, unknown>>) => {
          const value = scalarFilterValue(getValue(node));
          if (value === undefined) return;
          const key = valueKey(value);
          if (values.has(key)) return;
          const formatted = value === null
            ? '(Blanks)'
            : api.getCellValue({ rowNode: node, colKey: column, useFormatter: true });
          values.set(key, {
            value,
            label: formatted == null || formatted === '' ? String(value) : String(formatted),
          });
        });
        clientOptions = [...values.values()].sort((a, b) => {
          if (a.value === null) return -1;
          if (b.value === null) return 1;
          return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });
        });
      }

      const page = staticOptions
        ? staticGridFilterOptions(staticOptions, debouncedSearch, GRID_FILTER_OPTION_PAGE_SIZE, offset)
        : optionRequest
          ? await loadGridFilterOptions({
              ...optionRequest,
              search: debouncedSearch,
              limit: GRID_FILTER_OPTION_PAGE_SIZE,
              offset,
            })
          : staticGridFilterOptions(clientOptions ?? [], debouncedSearch, GRID_FILTER_OPTION_PAGE_SIZE, offset);
      if (generation !== requestGeneration.current) return;
      setOptions((current) => append ? [...current, ...page.options] : page.options);
      setHasMore(page.hasMore);
    } catch (loadError) {
      if (generation !== requestGeneration.current) return;
      setError(loadError instanceof Error ? loadError.message : 'Could not load filter choices');
      setHasMore(false);
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  }, [api, column, debouncedSearch, getValue, optionRequest, staticOptions]);

  useEffect(() => {
    void fetchPage(0, false);
  }, [fetchPage]);

  const selectAll = useCallback(() => onModelChange(null), [onModelChange]);
  const deselectAll = useCallback(() => onModelChange({ filterType: 'set', values: [] }), [onModelChange]);

  // Ticks or unticks the given choices, keeping whichever list (ticked or
  // unticked) the model already holds. An emptied exclusion list is "all".
  const setTicked = useCallback((targets: GridFilterOption[], tick: boolean) => {
    const targetKeys = new Set(targets.map((option) => valueKey(option.value)));
    if (allTicked) {
      if (tick || targets.length === 0) return;
      onModelChange({ filterType: 'set', values: targets.map((option) => option.value), excluded: true });
      return;
    }
    // Excluding: ticking removes from the unticked list. Including: unticking does.
    const removeFromList = excluding === tick;
    let next: GridFilterOptionValue[];
    if (removeFromList) {
      next = listedValues.filter((value) => !targetKeys.has(valueKey(value)));
    } else {
      next = [...listedValues];
      targets.forEach((option) => {
        if (!listedKeys.has(valueKey(option.value))) next.push(option.value);
      });
    }
    if (excluding && next.length === 0) {
      onModelChange(null);
      return;
    }
    onModelChange(excluding ? { filterType: 'set', values: next, excluded: true } : { filterType: 'set', values: next });
  }, [allTicked, excluding, listedKeys, listedValues, onModelChange]);

  const toggleOption = useCallback((option: GridFilterOption) => {
    setTicked([option], !isTicked(valueKey(option.value)));
  }, [isTicked, setTicked]);

  const allShownSelected = options.length > 0 && options.every((option) => isTicked(valueKey(option.value)));
  const toggleShown = useCallback(() => setTicked(options, !allShownSelected), [allShownSelected, options, setTicked]);

  const summary = allTicked
    ? 'All'
    : excluding
      ? `All but ${listedValues.length}`
      : listedValues.length === 0 ? 'None' : `${listedValues.length} selected`;

  useGridFilter({
    doesFilterPass: ({ node }) => isTicked(valueKey(scalarFilterValue(getValue(node)) ?? null)),
    getModelAsString: (currentModel: AsyncCheckboxFilterModel | null) => {
      if (!currentModel) return '';
      if (currentModel.excluded) return `All but ${currentModel.values.length}`;
      return currentModel.values.length ? `${currentModel.values.length} selected` : 'None';
    },
    afterGuiAttached: () => {
      void fetchPage(0, false);
    },
  });

  return (
    <div className="sky-checkbox-filter">
      <input
        className="sky-checkbox-filter__search"
        type="search"
        value={search}
        onChange={(event) => setSearch(event.currentTarget.value)}
        placeholder="Search choices…"
        aria-label="Search filter choices"
      />
      <div className="sky-checkbox-filter__actions">
        <button type="button" onClick={selectAll} disabled={allTicked}>
          Select all
        </button>
        <button type="button" onClick={deselectAll} disabled={!allTicked && !excluding && listedValues.length === 0}>
          Deselect all
        </button>
        {search.trim() && (
          <button type="button" onClick={toggleShown} disabled={options.length === 0}>
            {allShownSelected ? 'Deselect shown' : 'Select shown'}
          </button>
        )}
        <span>{summary}</span>
      </div>
      <div className="sky-checkbox-filter__options" role="group" aria-label="Filter choices">
        {options.map((option) => (
          <label className="sky-checkbox-filter__option" key={`${valueKey(option.value)}:${option.label}`}>
            <input
              type="checkbox"
              checked={isTicked(valueKey(option.value))}
              onChange={() => toggleOption(option)}
            />
            <span title={option.label}>{option.label}</span>
          </label>
        ))}
        {!loading && options.length === 0 && !error && (
          <div className="sky-checkbox-filter__message">No choices found</div>
        )}
        {error && (
          <div className="sky-checkbox-filter__message sky-checkbox-filter__message--error">
            <span>{error}</span>
            <button type="button" onClick={() => void fetchPage(0, false)}>Retry</button>
          </div>
        )}
        {loading && <div className="sky-checkbox-filter__message">Loading…</div>}
      </div>
      {hasMore && (
        <button
          className="sky-checkbox-filter__load-more"
          type="button"
          disabled={loading}
          onClick={() => void fetchPage(options.length, true)}
        >
          Load more
        </button>
      )}
    </div>
  );
}

export default AsyncCheckboxFilter;
